/* eslint-disable no-unused-vars */
/** @module Auth0PostLoginHandlerSeDash */
/* eslint-disable import/extensions */
import Auth0PostLoginHandlerBase from '../auth0-post-login-handler-base.mst.mjs';
import Auth0TokenStorageHelper from '../auth0-token-storage-helper.mjs';
import Auth0UtilsBrowser from '../../../../utils/auth0-utils-browser.mjs';

class Auth0PostLoginHandlerShipStationApiDash extends Auth0PostLoginHandlerBase {
  constructor(options) {
    super(options);
    this._options = options;
    this.SSAPIDashUrl = 'https://ss-api-portal-dev.kubedev.sslocal.com';
  }

  _shouldHandleSync = (loginData) => {
    console.log(`Auth0PostLoginHandlerShipStationApiDash._shouldHandleSync => evaluating (loginClientId=${this._options.clientId ?? 'none'})`);
    // Case 1 (existing): login initiated from this app's own client.
    if (this._options.clientId === 'HK9mNchVFvmG6I26hYizhNldaftpIVy1') {
      console.log('Auth0PostLoginHandlerShipStationApiDash._shouldHandleSync => case 1 matched (own client)');
      return true;
    }

    // Case 2: login from the ShipStation client by a ShipEngine-native account
    // without ShipStation access — claimed and redirected to the dashboard.
    return this._isCrossAppClaimLogin(loginData);
  };

  // Single source of truth for the cross-app criteria — used by both shouldHandle
  // and run (run re-validates rather than trusting handler selection).
  // The claim is a JSON-serialized object: { account_app, accessible_apps: [...] }.
  _isCrossAppClaimLogin = (loginData) => {
    const appAccess = this._parseAppAccessClaim(loginData);
    if (!appAccess) {
      return false;
    }
    // Identity check (the account IS ShipEngine-native) + access check (no ShipStation).
    const isClaimed = appAccess.account_app === 'ShipEngine'
      && !appAccess.accessible_apps.includes('ShipStation');
    console.log(`Auth0PostLoginHandlerShipStationApiDash => cross-app criteria: account_app=${appAccess.account_app}, accessible_apps=[${appAccess.accessible_apps.join(',')}], shipStationIncluded=${appAccess.accessible_apps.includes('ShipStation')}, claimed=${isClaimed}`);
    return isClaimed;
  };

  // Malformed/absent claim → null → not claimed (failsafe: falls through as before).
  _parseAppAccessClaim = (loginData) => {
    const raw = Auth0UtilsBrowser.getUserClaim('app_access', loginData?.user);
    if (!raw || typeof raw !== 'string') {
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      const result = (parsed && Array.isArray(parsed.accessible_apps)) ? parsed : null;
      console.log(`Auth0PostLoginHandlerShipStationApiDash => app_access claim parsed: ${result ? raw : 'invalid structure (no accessible_apps array)'}`);
      return result;
    } catch (e) {
      console.warn('Auth0PostLoginHandlerShipStationApiDash => malformed app_access claim, not claimed');
      return null;
    }
  };

  /**
   * @inheritdoc
   */
  async shouldHandle(loginData) {
    return Promise.resolve(this._shouldHandleSync(loginData));
  }

  /**
   * @inheritdoc
   */
  async run(loginData) {
    console.log(`Auth0PostLoginHandlerShipStationApiDash.run() => started (loginClientId=${this._options.clientId ?? 'none'})`);
    const { targetUrl: rawTargetUrl } = loginData.appState || {};

    // Case 2: claimed from the ShipStation login flow by app_access claim —
    // re-validate the criteria (don't trust handler selection alone), store tokens under
    // this app's cookie names on the ShipStation base domain and go straight to the
    // dashboard. No targetUrl logic applies (the login did not originate here).
    if (this._options.clientId !== 'HK9mNchVFvmG6I26hYizhNldaftpIVy1' && this._isCrossAppClaimLogin(loginData)) {
      console.log('Auth0PostLoginHandlerShipStationApiDash.run() => cross-app claim, storing tokens and redirecting to dashboard');
      if (loginData.tokens) {
        // Derive the cookie domain from the dashboard URL (eTLD+1) — it differs per
        // environment: .shipstation.com (prod) vs .sslocal.com (intg/stage).
        let cookieDomain;
        try {
          const hostParts = new URL(this.SSAPIDashUrl).hostname.split('.');
          if (hostParts.length >= 2) {
            cookieDomain = `.${hostParts.slice(-2).join('.')}`;
          }
        } catch (e) { /* unparseable dashboard URL — storeTokens will use the page domain */ }
        console.log(`Auth0PostLoginHandlerShipStationApiDash.run() => cookieDomain=${cookieDomain ?? '(page domain — dashboard URL unparseable)'}, dashboardUrl=${decodeURIComponent(this.SSAPIDashUrl)}`);

        Auth0TokenStorageHelper.storeTokens(loginData.tokens, [
          {
            storageType: 'cookie',
            accessTokenName: 'se_dash_access_token',
            refreshTokenName: 'se_dash_refresh_token',
            idTokenName: 'se_dash_id_token',
            ...(cookieDomain ? { cookieDomain } : {}),
          },
        ]);
      }
      window.location.href = decodeURIComponent(this.SSAPIDashUrl);
      return;
    }

    console.log('Auth0PostLoginHandlerShipStationApiDash.run() => if targetUrl is specified, that\'s where the user attempted to navigate before auth.');

    // Only honor a targetUrl that shares the current page's base domain — this CDN script only
    // ever runs on a domain we control, so window.location's base domain can't be forged by an
    // attacker-supplied targetUrl (e.g. a phishing lure passed via ?targetUrl=).
    const targetUrl = (rawTargetUrl && Auth0UtilsBrowser.isUrlOnCurrentBaseDomain(rawTargetUrl))
      ? rawTargetUrl
      : undefined;
    if (rawTargetUrl && !targetUrl) {
      console.warn(`Auth0PostLoginHandlerShipStationApiDash.run() => rejecting targetUrl on foreign domain => ${rawTargetUrl}`);
    }

    // Use app-specified tokenStorageConfig if provided, else default (_lat/_latv3).
    // Inject cookieDomain derived from appLoginUrl so cookies land on the app's domain
    // even when Auth0 runs on a different TLD (e.g. signin-stage.shipstation.com vs *.sslocal.com in intg).
    if (loginData.tokens) {
      if (this._tokenStorageConfig) {
        let cookieDomain;
        try {
          const hostParts = new URL(this._appLoginUrl).hostname.split('.');
          if (hostParts.length >= 2) {
            cookieDomain = `.${hostParts.slice(-2).join('.')}`;
          }
        } catch (e) { /* fall back to page domain */ }

        const config = cookieDomain
          ? this._tokenStorageConfig.map(c => ({ ...c, cookieDomain }))
          : this._tokenStorageConfig;
        Auth0TokenStorageHelper.storeTokens(loginData.tokens, config);
      } else {
        Auth0TokenStorageHelper.storeTokensDefault(loginData.tokens);
      }
    }

    if (targetUrl) {
      console.log(`targetUrl => ${targetUrl}`);
      window.location.href = targetUrl;
    } else {
      console.log('Auth0PostLoginHandlerShipStationApiDash.run() => If not present, redirect to default SS API Dash URL.');
      console.log(`SSAPIDashUrl => ${decodeURIComponent(this.SSAPIDashUrl)}`);
      window.location.href = decodeURIComponent(this.SSAPIDashUrl);
    }
  }
}

export default Auth0PostLoginHandlerShipStationApiDash;
